import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loading } from 'tdesign-react';
import axios from 'axios';
import { probeCurrentUser, type CurrentUser } from '@/services/user';
import { getPortalToken } from '@/utils/auth';

interface Props {
  children: ReactNode;
}

type GuardState = 'pending' | 'admin' | 'guest' | 'forbidden';

/**
 * 这里和 PortalGuard / LoginEntryGate 保持同一份占位 ID 黑名单：
 * 太湖未配置时后端可能回落到 'local-dev' 之类的占位身份，前端一律视为未登录。
 */
const ADMIN_PLACEHOLDER_IDS = new Set(['', 'local-dev']);

/**
 * 当前太湖登录用户上下文：
 * - 由 AdminGuard 在 /user/me 探测成功后注入
 * - Layout / 业务页可直接 useAdminUser() 取用，避免重复请求 /user/me，
 *   也避免 Layout 在身份未确认前先行渲染导致的无权限闪现
 */
const AdminUserContext = createContext<CurrentUser | null>(null);

export const useAdminUser = (): CurrentUser | null => useContext(AdminUserContext);

/**
 * 后台路由守卫
 *
 * 准入逻辑（同步阻塞，确认前不渲染 Layout）：
 * 1. 本地存在门户 token → 这是微信用户，直接重定向 /portal，避免后台页面闪现。
 * 2. 调 /user/me 探测太湖身份：
 *    - 拿到非占位 userId → 通过，注入上下文渲染 children。
 *    - 403（不在 ADMIN_USERS 白名单）→ 跳 /403，不加载页面详情。
 *    - 401 / 占位 userId / 其他错误 → 跳 /portal/login。
 *
 * 探测期间整页用 Loading 占位（menu + content 都不渲染），
 * 避免 "先进 /accounts → 接口 401 → 再跳登录" 的短暂暴露窗口。
 */
const AdminGuard = ({ children }: Props) => {
  const hasPortalToken = !!getPortalToken();
  const [state, setState] = useState<GuardState>(hasPortalToken ? 'guest' : 'pending');
  const [user, setUser] = useState<CurrentUser | null>(null);

  useEffect(() => {
    if (hasPortalToken) return;

    let mounted = true;
    const probe = async () => {
      try {
        const u = await probeCurrentUser();
        if (!mounted) return;
        if (u && !ADMIN_PLACEHOLDER_IDS.has(u.userId)) {
          setUser(u);
          setState('admin');
          return;
        }
      } catch (err) {
        if (!mounted) return;
        // 403：身份合法但不在 ADMIN_USERS 白名单 → 跳无权限页（不再回登录页骚扰用户）
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setState('forbidden');
          return;
        }
        /* 401 / 网络异常 / 太湖未配置 → 落到 guest */
      }
      if (mounted) setState('guest');
    };

    void probe();
    return () => {
      mounted = false;
    };
  }, [hasPortalToken]);

  // 微信用户：直接踢回门户
  if (hasPortalToken) return <Navigate to="/portal" replace />;

  // 探测中：整页 Loading，menu / content 都不渲染
  if (state === 'pending') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loading text="登录态校验中..." />
      </div>
    );
  }

  if (state === 'forbidden') return <Navigate to="/403" replace />;
  if (state === 'guest') return <Navigate to="/portal/login" replace />;

  return <AdminUserContext.Provider value={user}>{children}</AdminUserContext.Provider>;
};

export default AdminGuard;
